import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, BookOpen, Search, Check, ListChecks, CheckSquare, Square, Zap } from 'lucide-react';
import { Menu } from '../../../services/menus/menusService';
import { CostPriceAnalysis } from '../../../services/costPrice/costPriceService';
import { CostPriceCategory } from '../../../services/costPrice/costPriceCategoriesService';

type Tab = 'items' | 'add';

interface MenuDetailProps {
  menu: Menu;
  items: CostPriceAnalysis[];
  allCostPriceItems: CostPriceAnalysis[];
  menuItemIds: string[];
  categories: CostPriceCategory[];
  itemPriceMap: Record<string, { price: number; margin: number }>;
  // Names of revenue items sold in the last 30 days. Used to filter available
  // items by "active" status in the Add Items tab. Matched against menuItemName
  // (not costPriceName) since costPriceName can be a custom user-edited label.
  activeDishNames: Set<string>;
  onAddItems: (costPriceIds: string[]) => Promise<void>;
  onRemoveItem: (costPriceId: string) => void;
  onRemoveItems: (costPriceIds: string[]) => void;
  onToggleActive: () => void;
}

const MenuDetail: React.FC<MenuDetailProps> = ({
  menu,
  items,
  allCostPriceItems,
  menuItemIds,
  categories,
  itemPriceMap,
  activeDishNames,
  onAddItems,
  onRemoveItem,
  onRemoveItems,
  onToggleActive,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<Tab>('items');
  const [search, setSearch] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);

  // Build a map of categoryId → category for quick lookup
  const categoryMap = useMemo(() => {
    const map: Record<string, CostPriceCategory> = {};
    categories.forEach((c) => { map[c.id] = c; });
    return map;
  }, [categories]);

  // --- Add Items tab ---
  const existingSet = useMemo(() => new Set(menuItemIds), [menuItemIds]);

  const availableItems = useMemo(() => {
    return allCostPriceItems.filter((item) => !existingSet.has(item.id));
  }, [allCostPriceItems, existingSet]);

  // Categories that actually have available items
  const availableCategories = useMemo(() => {
    const catIds = new Set(availableItems.map((i) => i.categoryId).filter(Boolean));
    return categories.filter((c) => catIds.has(c.id));
  }, [availableItems, categories]);

  const filteredAvailableItems = useMemo(() => {
    let list = availableItems;
    if (selectedCategoryId !== 'all') {
      list = list.filter((i) => i.categoryId === selectedCategoryId);
    }
    if (search) {
      const lower = search.toLowerCase();
      list = list.filter(
        (i) =>
          i.costPriceName.toLowerCase().includes(lower) ||
          (i.menuItemName && i.menuItemName.toLowerCase().includes(lower))
      );
    }
    return list;
  }, [availableItems, selectedCategoryId, search]);

  const toggleItem = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      filteredAvailableItems.forEach((i) => next.add(i.id));
      return next;
    });
  };

  const deselectAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      filteredAvailableItems.forEach((i) => next.delete(i.id));
      return next;
    });
  };

  const allVisibleSelected = filteredAvailableItems.length > 0 && filteredAvailableItems.every((i) => selectedIds.has(i.id));

  // Subset of currently-visible items considered "active" (sold in the last 30 days).
  // We match on menuItemName (the POS/revenue-side link), not costPriceName.
  const activeVisibleItems = useMemo(
    () => filteredAvailableItems.filter((i) => !!i.menuItemName && activeDishNames.has(i.menuItemName)),
    [filteredAvailableItems, activeDishNames]
  );

  const allVisibleActiveSelected =
    activeVisibleItems.length > 0 && activeVisibleItems.every((i) => selectedIds.has(i.id));

  const selectAllVisibleActive = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      activeVisibleItems.forEach((i) => next.add(i.id));
      return next;
    });
  };

  const deselectAllVisibleActive = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      activeVisibleItems.forEach((i) => next.delete(i.id));
      return next;
    });
  };

  const handleAddSelected = async () => {
    if (selectedIds.size === 0) return;
    setIsSaving(true);
    try {
      await onAddItems(Array.from(selectedIds));
      setSelectedIds(new Set());
      setActiveTab('items');
    } catch {
      // handled upstream
    } finally {
      setIsSaving(false);
    }
  };

  // --- Current Items tab: search + bulk remove ---
  const [itemsSearch, setItemsSearch] = useState('');
  const [selectedRemoveIds, setSelectedRemoveIds] = useState<Set<string>>(new Set());

  const filteredItems = useMemo(() => {
    if (!itemsSearch) return items;
    const lower = itemsSearch.toLowerCase();
    return items.filter(
      (i) =>
        i.costPriceName.toLowerCase().includes(lower) ||
        (i.menuItemName && i.menuItemName.toLowerCase().includes(lower))
    );
  }, [items, itemsSearch]);

  // Group filtered items by category, maintaining category order
  const groupedItems = useMemo(() => {
    const groups: { category: CostPriceCategory | null; items: CostPriceAnalysis[] }[] = [];
    const catOrder = new Map(categories.map((c, i) => [c.id, i]));

    // Bucket items by categoryId
    const buckets = new Map<string, CostPriceAnalysis[]>();
    const uncategorized: CostPriceAnalysis[] = [];
    for (const item of filteredItems) {
      if (item.categoryId && categoryMap[item.categoryId]) {
        const list = buckets.get(item.categoryId) || [];
        list.push(item);
        buckets.set(item.categoryId, list);
      } else {
        uncategorized.push(item);
      }
    }

    // Sort buckets by category position order
    const sortedCatIds = Array.from(buckets.keys()).sort(
      (a, b) => (catOrder.get(a) ?? 999) - (catOrder.get(b) ?? 999)
    );
    for (const catId of sortedCatIds) {
      groups.push({ category: categoryMap[catId], items: buckets.get(catId)! });
    }
    if (uncategorized.length > 0) {
      groups.push({ category: null, items: uncategorized });
    }

    return groups;
  }, [filteredItems, categories, categoryMap]);

  const toggleRemoveItem = (id: string) => {
    setSelectedRemoveIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBulkRemove = () => {
    if (selectedRemoveIds.size === 0) return;
    onRemoveItems(Array.from(selectedRemoveIds));
    setSelectedRemoveIds(new Set());
  };

  // Reset state when switching tabs
  const switchTab = (tab: Tab) => {
    setActiveTab(tab);
    setSearch('');
    setSelectedCategoryId('all');
    setSelectedIds(new Set());
    setItemsSearch('');
    setSelectedRemoveIds(new Set());
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-5 py-4 border-b border-zinc-200 dark:border-zinc-700">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
              {menu.name}
            </h2>
            <div className="flex items-center gap-2 mt-1">
              <button
                onClick={onToggleActive}
                className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full cursor-pointer transition-colors ${
                  menu.isActive
                    ? 'bg-[#EDFCF5] text-[#0E8965] dark:bg-[#0E8965]/20 dark:text-[#0E8965] hover:bg-[#d1fae5] dark:hover:bg-[#0E8965]/30'
                    : 'bg-zinc-100 text-zinc-500 dark:bg-zinc-700 dark:text-zinc-400 hover:bg-zinc-200 dark:hover:bg-zinc-600'
                }`}
              >
                {menu.isActive ? t('menus.active') : t('menus.inactive')}
              </button>
              <span className="text-xs text-zinc-500 dark:text-zinc-400">
                {t('menus.itemCount', { count: items.length })}
              </span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-zinc-100 dark:bg-zinc-800 rounded-xl p-1">
          <button
            onClick={() => switchTab('items')}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeTab === 'items'
                ? 'bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100 shadow-sm'
                : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300'
            }`}
          >
            <ListChecks className="w-4 h-4" />
            {t('menus.items')} ({items.length})
          </button>
          <button
            onClick={() => switchTab('add')}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeTab === 'add'
                ? 'bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100 shadow-sm'
                : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300'
            }`}
          >
            <Plus className="w-4 h-4" />
            {t('menus.addItems')}
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'items' ? (
        /* ========== CURRENT ITEMS TAB ========== */
        <div className="flex-1 flex flex-col overflow-hidden">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center flex-1 text-zinc-400 dark:text-zinc-500">
              <BookOpen className="w-12 h-12 mb-3" />
              <p className="text-sm">{t('menus.noItems')}</p>
              <button
                onClick={() => switchTab('add')}
                className="mt-3 flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white dark:text-white bg-green-600 dark:bg-green-600 rounded-xl hover:bg-green-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                {t('menus.addItems')}
              </button>
            </div>
          ) : (
            <>
              {/* Search + bulk actions bar */}
              <div className="px-4 py-3 border-b border-zinc-100 dark:border-zinc-800 flex items-center gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 dark:text-zinc-500" />
                  <input
                    type="text"
                    value={itemsSearch}
                    onChange={(e) => setItemsSearch(e.target.value)}
                    placeholder={t('menus.searchItems')}
                    className="w-full pl-9 pr-3 py-2 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800 text-sm text-zinc-900 dark:text-zinc-100 outline-none focus:ring-2 focus:ring-green-500/40 dark:focus:ring-green-500/40 focus:border-green-400"
                  />
                </div>
                {selectedRemoveIds.size > 0 && (
                  <button
                    onClick={handleBulkRemove}
                    className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400 rounded-xl hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    Remove ({selectedRemoveIds.size})
                  </button>
                )}
              </div>

              {/* Column headers */}
              <div className="flex items-center gap-3 px-4 py-1.5 border-b border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/40">
                <div className="flex-shrink-0 w-[18px]" />
                <div className="min-w-0 flex-1 text-[11px] font-medium uppercase tracking-wider text-zinc-400 dark:text-zinc-500">
                  Item
                </div>
                <span className="text-[11px] font-medium uppercase tracking-wider text-zinc-400 dark:text-zinc-500 flex-shrink-0 w-16 text-right">
                  Price
                </span>
                <span className="text-[11px] font-medium uppercase tracking-wider text-zinc-400 dark:text-zinc-500 flex-shrink-0 w-16 text-right">
                  Cost
                </span>
                <span className="text-[11px] font-medium uppercase tracking-wider text-zinc-400 dark:text-zinc-500 flex-shrink-0 w-14 text-right">
                  Margin
                </span>
                <div className="w-[28px] flex-shrink-0" />
              </div>

              {/* Items list grouped by category */}
              <div className="flex-1 overflow-y-auto">
                {groupedItems.map((group) => (
                  <div key={group.category?.id || '__uncategorized'}>
                    {/* Section header */}
                    <div className="sticky top-0 z-10 flex items-center gap-2 px-4 py-2 bg-zinc-50 dark:bg-zinc-800/60 border-b border-zinc-100 dark:border-zinc-800">
                      {group.category ? (
                        <>
                          <div
                            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: group.category.color }}
                          />
                          <span className="text-xs font-semibold uppercase tracking-wider text-zinc-600 dark:text-zinc-300">
                            {group.category.name}
                          </span>
                        </>
                      ) : (
                        <span className="text-xs font-semibold uppercase tracking-wider text-zinc-400 dark:text-zinc-500">
                          Uncategorized
                        </span>
                      )}
                      <span className="text-xs text-zinc-400 dark:text-zinc-500">
                        ({group.items.length})
                      </span>
                    </div>

                    {/* Items in this section */}
                    {group.items.map((item) => {
                      const priceInfo = itemPriceMap[item.id];
                      const price = priceInfo?.price || 0;
                      const margin = priceInfo?.margin || 0;
                      return (
                        <div
                          key={item.id}
                          className="flex items-center gap-3 px-4 py-2.5 border-b border-zinc-50 dark:border-zinc-800/50 hover:bg-zinc-50 dark:hover:bg-zinc-800/30 group"
                        >
                          {/* Checkbox */}
                          <button
                            onClick={() => toggleRemoveItem(item.id)}
                            className="flex-shrink-0"
                          >
                            {selectedRemoveIds.has(item.id) ? (
                              <CheckSquare className="w-4.5 h-4.5 text-red-500 dark:text-textRed-dark" />
                            ) : (
                              <Square className="w-4.5 h-4.5 text-zinc-300 dark:text-zinc-600 group-hover:text-zinc-400" />
                            )}
                          </button>

                          {/* Item info */}
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">
                              {item.costPriceName}
                            </p>
                            {item.menuItemName && item.menuItemName !== item.costPriceName && (
                              <p className="text-xs text-zinc-500 dark:text-zinc-400 truncate">
                                {item.menuItemName}
                              </p>
                            )}
                          </div>

                          {/* Price */}
                          <span className="text-sm text-zinc-600 dark:text-zinc-400 tabular-nums flex-shrink-0 w-16 text-right">
                            {price > 0 ? `€${price.toFixed(2)}` : '-'}
                          </span>

                          {/* Cost */}
                          <span className="text-sm text-zinc-600 dark:text-zinc-400 tabular-nums flex-shrink-0 w-16 text-right">
                            {item.totalCost > 0 ? `€${item.totalCost.toFixed(2)}` : '-'}
                          </span>

                          {/* Margin */}
                          <span className={`text-sm tabular-nums flex-shrink-0 w-14 text-right font-medium ${
                            margin > 0
                              ? margin >= 70 ? 'text-emerald-600 dark:text-emerald-400' : margin >= 50 ? 'text-amber-600 dark:text-amber-400' : 'text-red-500 dark:text-red-400'
                              : 'text-zinc-400 dark:text-zinc-500'
                          }`}>
                            {price > 0 ? `${margin.toFixed(0)}%` : '-'}
                          </span>

                          {/* Remove */}
                          <button
                            onClick={() => onRemoveItem(item.id)}
                            className="p-1 rounded-lg text-zinc-300 dark:text-zinc-600 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors opacity-0 group-hover:opacity-100"
                            title={t('menus.removeItems')}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      ) : (
        /* ========== ADD ITEMS TAB ========== */
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Search bar */}
          <div className="px-4 pt-3 pb-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 dark:text-zinc-500" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('menus.searchItems')}
                className="w-full pl-9 pr-3 py-2 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800 text-sm text-zinc-900 dark:text-zinc-100 outline-none focus:ring-2 focus:ring-green-500/40 dark:focus:ring-green-500/40 focus:border-green-400"
                autoFocus
              />
            </div>
          </div>

          {/* Category filter pills */}
          {availableCategories.length > 0 && (
            <div className="px-4 pb-2 flex gap-1.5 flex-wrap">
              <button
                onClick={() => setSelectedCategoryId('all')}
                className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                  selectedCategoryId === 'all'
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400 hover:bg-zinc-200 dark:hover:bg-zinc-700'
                }`}
              >
                All
              </button>
              {availableCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategoryId(cat.id)}
                  className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                    selectedCategoryId === cat.id
                      ? 'ring-1 ring-offset-1 dark:ring-offset-zinc-900'
                      : 'hover:opacity-80'
                  }`}
                  style={{
                    backgroundColor: `${cat.color}20`,
                    color: cat.color,
                    ...(selectedCategoryId === cat.id ? { ringColor: cat.color } : {}),
                  }}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          )}

          {/* Select all / Select active + count */}
          <div className="px-4 pb-2 flex items-center justify-between">
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={allVisibleSelected ? deselectAllVisible : selectAllVisible}
                className="flex items-center gap-1.5 text-xs font-medium text-zinc-500 dark:text-zinc-400 hover:text-green-600 dark:hover:text-green-400 transition-colors"
              >
                {allVisibleSelected ? (
                  <CheckSquare className="w-3.5 h-3.5" />
                ) : (
                  <Square className="w-3.5 h-3.5" />
                )}
                {allVisibleSelected ? t('menus.deselectAll') : t('menus.selectAll')}
                {filteredAvailableItems.length > 0 && ` (${filteredAvailableItems.length})`}
              </button>
              <button
                onClick={allVisibleActiveSelected ? deselectAllVisibleActive : selectAllVisibleActive}
                disabled={activeVisibleItems.length === 0}
                title={activeVisibleItems.length === 0 ? t('menus.noActiveItemsTooltip') : undefined}
                className="flex items-center gap-1.5 text-xs font-medium text-zinc-500 dark:text-zinc-400 hover:text-green-600 dark:hover:text-green-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-zinc-500 dark:disabled:hover:text-zinc-400"
              >
                {allVisibleActiveSelected ? (
                  <CheckSquare className="w-3.5 h-3.5" />
                ) : (
                  <Zap className="w-3.5 h-3.5" />
                )}
                {allVisibleActiveSelected ? t('menus.deselectActive') : t('menus.selectActive')}
                {activeVisibleItems.length > 0 && ` (${activeVisibleItems.length})`}
              </button>
            </div>
            {selectedIds.size > 0 && (
              <span className="text-xs font-medium text-green-600 dark:text-green-400">
                {selectedIds.size} selected
              </span>
            )}
          </div>

          {/* Available items list */}
          <div className="flex-1 overflow-y-auto border-t border-zinc-100 dark:border-zinc-800">
            {filteredAvailableItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-zinc-400 dark:text-zinc-500">
                <Search className="w-10 h-10 mb-2" />
                <p className="text-sm">No items found</p>
              </div>
            ) : (
              filteredAvailableItems.map((item) => {
                const isSelected = selectedIds.has(item.id);
                const cat = item.categoryId ? categoryMap[item.categoryId] : null;
                return (
                  <button
                    key={item.id}
                    onClick={() => toggleItem(item.id)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors border-b border-zinc-50 dark:border-zinc-800/50 ${
                      isSelected
                        ? 'bg-green-50/50 dark:bg-green-900/10'
                        : 'hover:bg-zinc-50 dark:hover:bg-zinc-800/30'
                    }`}
                  >
                    {/* Checkbox */}
                    <div
                      className={`w-4.5 h-4.5 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${
                        isSelected
                          ? 'bg-green-500 border-green-500'
                          : 'border-zinc-300 dark:border-zinc-600'
                      }`}
                    >
                      {isSelected && <Check className="w-3 h-3 text-white dark:text-white" />}
                    </div>

                    {/* Item info */}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">
                        {item.costPriceName}
                      </p>
                      {item.menuItemName && item.menuItemName !== item.costPriceName && (
                        <p className="text-xs text-zinc-500 dark:text-zinc-400 truncate">
                          {item.menuItemName}
                        </p>
                      )}
                    </div>

                    {/* Category badge */}
                    {cat && (
                      <span
                        className="inline-flex px-2 py-0.5 text-[11px] font-medium rounded-full flex-shrink-0"
                        style={{ backgroundColor: `${cat.color}20`, color: cat.color }}
                      >
                        {cat.name}
                      </span>
                    )}

                    {/* Cost */}
                    <span className="text-xs text-zinc-500 dark:text-zinc-400 tabular-nums flex-shrink-0 w-14 text-right">
                      {item.totalCost > 0 ? `€${item.totalCost.toFixed(2)}` : '-'}
                    </span>
                  </button>
                );
              })
            )}
          </div>

          {/* Sticky add button */}
          {selectedIds.size > 0 && (
            <div className="px-4 py-3 border-t border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900">
              <button
                onClick={handleAddSelected}
                disabled={isSaving}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white dark:text-white bg-green-600 dark:bg-green-600 rounded-xl hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Plus className="w-4 h-4" />
                {isSaving ? t('common.saving') : `${t('menus.addItems')} (${selectedIds.size})`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MenuDetail;
